import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function WebsiteDetailModal({
  open,
  onClose,
  websiteId,
  websiteDisplayId,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open || !websiteId) {
      setData(null);
      setError(null);
      return;
    }

    const fetchWebsite = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const token = await getAccessTokenSilently().catch(() => null);
        const response = await fetch(
          `${API_BASE_URL}/admin/squarespace/websites/${encodeURIComponent(websiteId)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );

        const responseData = await response.json();

        if (!response.ok) {
          setError(responseData.message || responseData.error || `Failed to load website: ${response.status}`);
          return;
        }

        setData(responseData.data || responseData);
      } catch (err) {
        setError(err.message || "Failed to load website details");
      } finally {
        setLoading(false);
      }
    };

    fetchWebsite();
  }, [open, websiteId, getAccessTokenSilently]);

  const handleClose = () => {
    setData(null);
    setError(null);
    onClose();
  };

  const jsonString = data != null ? JSON.stringify(data, null, 2) : "";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Website Details {websiteDisplayId ? `– ${websiteDisplayId}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && data != null && (
          <Box
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.grey[900]
                  : theme.palette.grey[100],
              borderRadius: 1,
              p: 2,
              fontFamily: "monospace",
              fontSize: "0.8rem",
              overflow: "auto",
              maxHeight: "60vh",
            }}
          >
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {jsonString}
            </pre>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
